// cspell:ignore Conzuri

import type { Money } from "@redotech/money";
import { moneyFormat } from "@redotech/money/util";
import { IterableMap } from "@redotech/react-util/component";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import { ProductImageAspectRatio } from "@redotech/redo-model/image";
import { getCarrierDisplayName } from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import type {
  TrackingPageLineItem,
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import classNames from "classnames";
import { memo } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { Card } from "../../card";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { withShopifyImageSize } from "../../utils/shopify-image";
import * as css from "./order.module.css";
import { PickupConfig } from "./pick-up-config-util";
import {
  ShipmentStatusDisplay,
  ShipmentStatusDisplayType,
} from "./shipment-status-display";

function getUnfulfilledItems(order: TrackingPageOrder): TrackingPageLineItem[] {
  const shippedQuantities = new Map<string, number>();
  for (const shipment of order.shipments) {
    for (const item of shipment.lineItems) {
      shippedQuantities.set(
        item.id,
        (shippedQuantities.get(item.id) ?? 0) + item.quantity,
      );
    }
  }
  return order.lineItems
    .filter((item) => (shippedQuantities.get(item.id) ?? 0) < item.quantity)
    .map((item) => ({
      ...item,
      quantity: item.quantity - (shippedQuantities.get(item.id) ?? 0),
    }));
}

export const OrderOrFulfillments = memo(function OrderOrFulfillments({
  order,
  showTracking = true,
  pickupConfig,
  handleTrackingPageButtonClickGenerator,
  storeUrl,
  imageAspectRatio,
  stalledDays,
}: {
  order: TrackingPageOrder;
  showTracking?: boolean;
  pickupConfig: PickupConfig;
  handleTrackingPageButtonClickGenerator: (
    fulfillmentNumber: number | null,
  ) => (event: React.MouseEvent) => void;
  storeUrl: string;
  imageAspectRatio: number | null;
  stalledDays: number | null;
}) {
  const { t } = useTranslation();
  const isMobile = useScreenType() === ScreenType.MOBILE;

  const unfulfilledItems =
    order.shipments.length > 0 ? getUnfulfilledItems(order) : [];
  const hasUnfulfilledItems = unfulfilledItems.length > 0;
  const shouldWrapInCards =
    (order.shipments.length > 1 || hasUnfulfilledItems) && !isMobile;

  return order.shipments.length > 0 && order.source !== "commentsold" ? (
    <Flex dir="column" gap="xl">
      <Text fontSize="lg" fontWeight="semibold">
        {t("Order details")}
      </Text>
      {order.shipments.map((shipment: TrackingPageShipment, index: number) => {
        const content = (
          <FulfillmentDetailsCard
            handleTrackingPageButtonClick={handleTrackingPageButtonClickGenerator(
              index,
            )}
            imageAspectRatio={imageAspectRatio}
            key={shipment.id}
            order={order}
            pickupConfig={pickupConfig}
            shipment={shipment}
            shipmentIndex={index}
            showTracking={showTracking}
            stalledDays={stalledDays}
            storeUrl={storeUrl}
          />
        );
        return shouldWrapInCards ? (
          <Card borderColor="secondary" key={shipment.id} radius="md">
            {content}
          </Card>
        ) : (
          content
        );
      })}
      {hasUnfulfilledItems &&
        (() => {
          const content = (
            <UnfulfilledItemsCard
              imageAspectRatio={imageAspectRatio}
              unfulfilledItems={unfulfilledItems}
            />
          );
          return shouldWrapInCards ? (
            <Card borderColor="secondary" radius="md">
              {content}
            </Card>
          ) : (
            content
          );
        })()}
    </Flex>
  ) : (
    <OrderDetailsCard
      handleTrackingPageButtonClick={handleTrackingPageButtonClickGenerator(
        null,
      )}
      imageAspectRatio={imageAspectRatio}
      order={order}
      pickupConfig={pickupConfig}
      showTracking={showTracking}
      stalledDays={stalledDays}
      storeUrl={storeUrl}
    />
  );
});

const OrderDetailsCard = memo(function OrderDetailsCard({
  order,
  handleTrackingPageButtonClick,
  pickupConfig,
  showTracking = true,
  storeUrl,
  imageAspectRatio,
  stalledDays,
}: {
  order: TrackingPageOrder;
  handleTrackingPageButtonClick: (event: React.MouseEvent) => void;
  pickupConfig: PickupConfig;
  storeUrl: string;
  showTracking?: boolean;
  imageAspectRatio: number | null;
  stalledDays: number | null;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Flex dir="column">
      <Text fontSize="lg" fontWeight="semibold">
        {t("Order details")}
      </Text>
      <Flex align="center" justify="space-between" mt="lg">
        <Flex align="center">
          <RedoIcon color="quaternary" Icon={ClockIcon} size={14} />
          <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
            <ShipmentStatusDisplay
              cancelReason={order.cancelReason}
              displayType={ShipmentStatusDisplayType.TEXT}
              pickupConfig={pickupConfig}
              source={order.sourceName}
              stalledDays={stalledDays}
              storeUrl={storeUrl}
            />
          </Text>
        </Flex>
        {showTracking && (
          <RedoButton
            hierarchy="primary"
            onClick={handleTrackingPageButtonClick}
            size="sm"
            text={t("Track package")}
          />
        )}
      </Flex>
      <Flex dir="column" gap="2xl" mt="md">
        <IterableMap
          items={(order.lineItems || []).filter((item) => item.quantity > 0)}
          keyFn={(item) => item.id}
        >
          {(item) => (
            <LineItem
              imageAspectRatio={imageAspectRatio}
              item={item}
              locale={i18n.language}
            />
          )}
        </IterableMap>
      </Flex>
    </Flex>
  );
});

const UnfulfilledItemsCard = memo(function UnfulfilledItemsCard({
  unfulfilledItems,
  imageAspectRatio,
}: {
  unfulfilledItems: TrackingPageLineItem[];
  imageAspectRatio: number | null;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Flex dir="column" gap="lg">
      <Text fontSize="sm">{t("Unfulfilled items")}</Text>
      <Flex dir="column" gap="2xl" mt="md">
        <IterableMap items={unfulfilledItems} keyFn={(item) => item.id}>
          {(item) => (
            <LineItem
              imageAspectRatio={imageAspectRatio}
              item={item}
              locale={i18n.language}
            />
          )}
        </IterableMap>
      </Flex>
    </Flex>
  );
});

const FulfillmentDetailsCard = memo(function FulfillmentDetailsCard({
  shipment,
  shipmentIndex,
  order,
  showTracking = true,
  handleTrackingPageButtonClick,
  pickupConfig,
  storeUrl,
  imageAspectRatio,
  stalledDays,
}: {
  shipment: TrackingPageShipment;
  shipmentIndex: number;
  order: TrackingPageOrder;
  showTracking?: boolean;
  handleTrackingPageButtonClick: (event: React.MouseEvent) => void;
  pickupConfig: PickupConfig;
  storeUrl: string;
  imageAspectRatio: number | null;
  stalledDays: number | null;
}) {
  const source = order.sourceName ?? "";

  const { t, i18n } = useTranslation();

  return (
    <>
      <Flex align="center" justify="space-between">
        <Flex dir="column" gap="lg" w="full">
          <Flex align="center" gap="md" justify="space-between" w="full">
            <Text fontSize="sm">
              {order.shipments.length === 1
                ? t("Shipment {{shipmentNumber}} of 1", {
                    shipmentNumber: shipmentIndex + 1,
                  })
                : t("Shipment {{shipmentNumber}}", {
                    shipmentNumber: shipmentIndex + 1,
                  })}
            </Text>
            {showTracking && (
              <RedoButton
                hierarchy="primary"
                onClick={handleTrackingPageButtonClick}
                size="sm"
                text={t("Track package")}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex dir="column" gap="none">
        <Flex dir="column" gap="2xl" mt="md">
          <IterableMap
            items={(shipment.lineItems || []).filter(
              (item) => item.quantity > 0,
            )}
            keyFn={(item) => item.id}
          >
            {(item) => (
              <LineItem
                imageAspectRatio={imageAspectRatio}
                item={item}
                locale={i18n.language}
              />
            )}
          </IterableMap>
        </Flex>
        {source !== "pos" && showTracking && (
          <Flex dir="column" gap="2xl" mt="3xl">
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
                {t("Status")}
              </Text>
              <ShipmentStatusDisplay
                cancelReason={order.cancelReason}
                displayType={ShipmentStatusDisplayType.BADGE}
                pickupConfig={pickupConfig}
                shipment={shipment}
                source={source}
                stalledDays={stalledDays}
                storeUrl={storeUrl}
              />
            </Flex>
            <Flex dir="column" flex={1} gap="xs">
              <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
                {t("Delivery method")}
              </Text>
              <Text fontSize="md" fontWeight="medium">
                {order.shippingMethod ?? t("Unknown")}
              </Text>
            </Flex>
            {shipment.carrier && (
              <Flex dir="column" flex={1} gap="xs">
                <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
                  {t("Carrier")}
                </Text>
                <Text fontSize="md" fontWeight="medium">
                  {replaceWordsByMerchantRequest(
                    getCarrierDisplayName(shipment.carrier),
                    storeUrl,
                  )}
                  {shipment.trackingCode && (
                    <>
                      :{" "}
                      {storeUrl === "shopconzuri.myshopify.com" ? (
                        <a href={shipment.trackingUrl ?? undefined}>
                          {shipment.trackingCode}
                        </a>
                      ) : (
                        shipment.trackingCode
                      )}
                    </>
                  )}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
});

export const LineItem = memo(function LineItem({
  item,
  imageAspectRatio,
  hidePrice = false,
  hideQuantity = false,
  headerStyles,
  bodyStyles,
  badgeStyles,
  locale,
}: {
  item: TrackingPageLineItem;
  imageAspectRatio: number | null;
  hidePrice?: boolean;
  hideQuantity?: boolean;
  headerStyles?: React.CSSProperties;
  bodyStyles?: React.CSSProperties;
  badgeStyles?: React.CSSProperties;
  locale?: string;
}) {
  const { t } = useTranslation();
  const isMobile = useScreenType(768) === ScreenType.MOBILE;

  // Thumbnails render at 64 CSS pixels; request a 2x asset from Shopify so
  // retina displays stay sharp without downloading the full ~1600px source.
  const image = withShopifyImageSize(item.imageUrl, { width: 128 });
  const displayQuantity = item.quantity;

  const hasDiscount =
    item.discountedPrice != null &&
    item.undiscountedPrice != null &&
    item.discountedPrice.amount < item.undiscountedPrice.amount;

  function formatMoney(money: Money | null | undefined) {
    if (!money) {
      return t("No price provided");
    }
    return moneyFormat(money, undefined, locale);
  }

  function getPriceTextComponent(isMobile: boolean) {
    if (hidePrice) {
      return null;
    }
    if (hasDiscount) {
      return (
        <>
          <Text
            className={isMobile ? css.priceTextMobile : css.priceText}
            fontSize="sm"
            fontWeight="medium"
            style={{ ...bodyStyles, textDecoration: "line-through" }}
          >
            {formatMoney(item.undiscountedPrice)}
          </Text>
          <Text
            className={isMobile ? css.priceTextMobile : css.priceText}
            fontSize="sm"
            fontWeight="medium"
            style={bodyStyles}
          >
            {formatMoney(item.discountedPrice)}
          </Text>
        </>
      );
    }
    return (
      <Text
        className={isMobile ? css.priceTextMobile : css.priceText}
        fontSize="sm"
        fontWeight="medium"
        style={bodyStyles}
      >
        {formatMoney(item.undiscountedPrice)}
      </Text>
    );
  }

  return (
    <Flex className={classNames(css.product, isMobile && css.productMobile)}>
      <div className={css.imageContainer}>
        <img
          className={css.productImage}
          src={image ?? undefined}
          style={{
            aspectRatio: imageAspectRatio ?? ProductImageAspectRatio.SQUARE,
          }}
        />
        {!hideQuantity && (
          <div className={css.quantityBadge} style={badgeStyles}>
            {displayQuantity}
          </div>
        )}
      </div>
      <Flex
        className={css.productInfo}
        flex={1}
        justify="space-between"
        w="full"
      >
        <Flex dir="column" gap="xs">
          <Text fontSize="md" fontWeight="medium" style={headerStyles}>
            {item.name}
          </Text>
          {item.variantTitle && item.variantTitle !== "Default Title" && (
            <Text fontSize="xs" style={bodyStyles} textColor="tertiary">
              {item.variantTitle}
            </Text>
          )}
          {getPriceTextComponent(true)}
        </Flex>
        <Flex align="flex-end" dir="column" gap="xs">
          {!hideQuantity && (
            <Text
              className={css.quantityText}
              fontSize="md"
              fontWeight="medium"
              style={bodyStyles}
            >
              {`x${displayQuantity}`}
            </Text>
          )}
          {getPriceTextComponent(false)}
        </Flex>
      </Flex>
    </Flex>
  );
});

export function replaceWordsByMerchantRequest(
  statusMessage: string,
  storeUrl: string,
) {
  if (storeUrl === "shopconzuri.myshopify.com") {
    statusMessage = statusMessage.replaceAll("Yanwen", "Conzuri");
    statusMessage = statusMessage.replaceAll("pickup scan", "order received");
    statusMessage = statusMessage.replaceAll(
      "information input",
      "delivery information",
    );
    statusMessage = statusMessage.replaceAll("carrier", "local carrier");
    statusMessage = statusMessage.replaceAll("HongKong", "Conzuri");
    statusMessage = statusMessage.replaceAll("hong kong", "Conzuri");
    statusMessage = statusMessage.replaceAll("Guangdong Province", "Conzuri");
    statusMessage = statusMessage.replaceAll("Guangdong", "Conzuri");
    statusMessage = statusMessage.replaceAll("Shipper", "Conzuri");
    statusMessage = statusMessage.replaceAll("received", "boxed up");
    statusMessage = statusMessage.replaceAll(
      "China Post",
      "International Carrier",
    );
    statusMessage = statusMessage.replaceAll(
      "Handed over by Customers",
      "International Carrier",
    );
    statusMessage = statusMessage.replaceAll(
      "Item boxed up",
      "item received at local carrier",
    );
    statusMessage = statusMessage.replaceAll("Asia", "Conzuri");
    statusMessage = statusMessage.replaceAll("China", "Conzuri");
    statusMessage = statusMessage.replaceAll(
      "/安排投递/",
      "arrange for delivery",
    );
    // Remove any east asian unicode characters
    statusMessage = statusMessage.replace(/[\u4E00-\u9FFF\u3400-\u4DBF]/g, "");
  }
  return statusMessage;
}
